import React from 'react';
import App from 'next/app';
import Router from 'next/router';
import { I18nextProvider } from 'react-i18next';
import { ApolloProvider } from '@apollo/client';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { Bridge } from '@digitaz/bridge-sdk-client';
import { ThemeWrapper } from '../styled/theme/wrapper';
import reduxWrapper from '../store';
import ErrorBoundary from '../components/ErrorBoundary';
import withApolloClient from '../utils/with-apollo-client';
import countersLoader from '../utils/counters';
import CONFIG from '../config';
import { HeadMeta } from '../components/HeadMeta';
import { ErrorPage } from '../components/ErrorPage';
import adriverCallback from '../components/Banner/genericCallback';
import Bugsnag from '../utils/bugsnag';
import '../styles/index.scss';
import { GlobalStyles } from '../styled/global.styled';
import { getBridgeConfig } from '../bridge/config';
import { ThemeType } from '../styled/theme/constants';
import { getInitialSeoData } from '../utils/helpers/seo';
import { getI18nForLang } from '../utils/helpers/getI18nForLang';
import { getLocaleFromBrowser, getLangDefinition } from '../utils/helpers';
import { PageContext } from '../context/PageContext';
import { GoogleMapsProvider } from '../providers/GoogleMapsProvider';

const HOME_PAGE_QUERY_NAMES = ['LocalData', 'BookmarkIds', 'CompanyName'];

const refetchHomePageQueries = apolloClient => {
  requestAnimationFrame(() => {
    apolloClient.refetchQueries({ include: HOME_PAGE_QUERY_NAMES });
  });
};

const { gtmContainerId, fbAppId } = CONFIG;
const { tagManager, li } = countersLoader;

function shouldForwardProp(propName, target) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}

const onlyProductionContent = renderContent => {
  if (window.location.hostname?.indexOf('bina.az') > -1) {
    renderContent();
  }
};

class MyApp extends App {
  timeoutId = null;

  analyticsStarted = false;

  interactionEvents = ['click', 'scroll', 'touchstart', 'keydown'];

  static async getInitialProps({ Component, ctx }) {
    let pageProps = {};
    const { store, pathname } = ctx;

    store.dispatch({ type: 'DRAWER_TOGGLE', payload: false });

    // Use request-scoped i18n instance set by withApolloClient
    // This prevents race conditions with the singleton
    const requestI18n =
      ctx.requestI18n || getI18nForLang(ctx.lng || CONFIG.defaultLocale);

    try {
      if (ctx && ctx.res) {
        ctx.res.setHeader(
          'Cache-Control',
          's-maxage=1, stale-while-revalidate=59, max-age=0, private, must-revalidate'
        );
      }
      pageProps = Component.getInitialProps
        ? await Component.getInitialProps(ctx, requestI18n)
        : {};
    } catch (error) {
      console.log(`Error occurred: ${error}`);

      Bugsnag.notify(error, event => {
        const componentName =
          Component.displayName || Component.name || 'Unknown';

        event.context = `getInitialProps:${componentName}`;

        event.groupingHash = `getInitialProps-${pathname}`;
        event.addMetadata('page', {
          pathname,
          component: componentName,
          isServer: typeof window === 'undefined',
          functionType: 'getInitialProps'
        });
      });

      pageProps.statusCode = 500;
      pageProps.seoData = {};
      pageProps.pathname = pathname;
    }

    const userAgent =
      typeof window === 'undefined'
        ? ctx.req?.headers['user-agent']
        : window.navigator.userAgent;

    pageProps.isSSRWebView = userAgent?.indexOf('miniApp') >= 0;

    return {
      pageProps
    };
  }

  trackFirstInteraction = () => {
    if (this.analyticsStarted) return;
    this.analyticsStarted = true;
    this.timeoutId = setTimeout(() => {
      onlyProductionContent(() => {
        li();
        tagManager(gtmContainerId);
      });
    }, 1000);
  };

  componentDidMount() {
    const { apolloClient } = this.props;
    NProgress.configure({ showSpinner: false, trickleSpeed: 50 });

    Router.router.events.on('routeChangeStart', url => {
      if (!url.match(/\/items\/(\d+)/)) {
        NProgress.start();
      }
    });

    if (typeof window !== 'undefined') {
      this.interactionEvents.forEach(evt => {
        document.addEventListener(evt, this.trackFirstInteraction, {
          once: true
        });
      });
    }

    Router.router.events.on('routeChangeComplete', url => {
      onlyProductionContent(() => li());
      NProgress.done();
      if (url === '/') {
        refetchHomePageQueries(apolloClient);
      }
      // Update lang attribute on HTML element after route change
      this.updateHtmlLang();
    });
    document.body.classList.remove('preload');
    // Set initial lang attribute
    this.updateHtmlLang();
  }

  componentDidUpdate(prevProps) {
    // Update lang attribute when language changes
    const { pageProps: prevPageProps, lng: prevLng } = prevProps;
    const { pageProps, lng } = this.props;
    const prevLang =
      prevPageProps?.currentLang || prevLng || CONFIG.defaultLocale;
    const currentLang = pageProps?.currentLang || lng || CONFIG.defaultLocale;

    if (prevLang !== currentLang) {
      this.updateHtmlLang();
    }
  }

  updateHtmlLang = () => {
    if (typeof window === 'undefined') return;

    const { pageProps, lng } = this.props;
    let currentLang = pageProps?.currentLang || lng;

    if (!currentLang) {
      currentLang = getLocaleFromBrowser();
    }

    currentLang = currentLang || CONFIG.defaultLocale;
    const langDefinition = getLangDefinition(currentLang);

    if (document.documentElement.lang !== langDefinition) {
      document.documentElement.lang = langDefinition;
    }
  };

  componentWillUnmount() {
    if (this.timeoutId) clearTimeout(this.timeoutId);

    if (typeof window !== 'undefined') {
      this.interactionEvents.forEach(evt => {
        document.removeEventListener(evt, this.trackFirstInteraction);
      });
    }
  }

  render() {
    const { Component, pageProps, apolloClient, router, lng } = this.props;
    const { seoData, statusCode, isSSRWebView, hideFromRobots } = pageProps;

    // Use thread-safe i18n instance for both SEO and components
    // Priority: pageProps.currentLang > lng (from withApolloClient) > browser detection > default
    let currentLang = pageProps?.currentLang || lng;

    // On client-side, detect from hostname if not available from props
    if (!currentLang && typeof window !== 'undefined') {
      currentLang = getLocaleFromBrowser();
    }

    currentLang = currentLang || CONFIG.defaultLocale;
    const threadSafeI18n = getI18nForLang(currentLang);

    const { seoConfig, seo } = getInitialSeoData(
      router,
      currentLang,
      seoData,
      fbAppId,
      threadSafeI18n
    );

    const getLayout = Component.getLayout || (page => page);

    if (typeof window !== 'undefined') {
      window.adriverExtentionLoad = adriverCallback;
    }

    if (statusCode === 404) {
      return (
        <I18nextProvider i18n={threadSafeI18n}>
          <ErrorPage statusCode={statusCode} />
        </I18nextProvider>
      );
    }

    return (
      <Bridge isServerWebView={isSSRWebView} config={getBridgeConfig()}>
        <PageContext.Provider value={pageProps}>
          <GoogleMapsProvider>
            <ThemeWrapper
              initialThemeType={pageProps?.themeType || ThemeType.light}
            >
              <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                <GlobalStyles />
                <I18nextProvider i18n={threadSafeI18n}>
                  {seoConfig ? (
                    <>
                      {!hideFromRobots && <DefaultSeo {...seoConfig} />}
                      <HeadMeta
                        hostSum={seo.hostSum}
                        hideFromRobots={hideFromRobots}
                        mainPageImage={pageProps?.mainPageImage}
                      />
                    </>
                  ) : null}
                  <ApolloProvider client={apolloClient}>
                    {getLayout(
                      <ErrorBoundary
                        statusCode={statusCode}
                        i18n={threadSafeI18n}
                        router={Router}
                        pageProps={pageProps}
                      >
                        <Component {...pageProps} />
                      </ErrorBoundary>,
                      pageProps
                    )}
                  </ApolloProvider>
                </I18nextProvider>
              </StyleSheetManager>
            </ThemeWrapper>
          </GoogleMapsProvider>
        </PageContext.Provider>
      </Bridge>
    );
  }
}

export default withApolloClient(reduxWrapper.withRedux(MyApp));
